import React, { useState, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  IoMedkitOutline,
  IoArrowBack,
  IoCheckmarkCircle,
  IoRocketOutline,
  IoCloseOutline,
  IoWarningOutline,
} from 'react-icons/io5';
import { AuthContext } from '../../../utils/context/auth';
import PDFViewer from './PDFViewer';
import { pdfDocuments } from '../../../data/pdfConfig';

const API_BASE_URL = "https://promedhealth-frontdoor-h4c4bkcxfkduezec.z02.azurefd.net/api/v1/";

const getAuthHeaders = () => {
  const token = localStorage.getItem('accessToken');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

const FastOrderFlow = ({ onBack, onSuccess, source, selectedProviderId, selectedProviderDetails }) => {
  const { getProvider } = useContext(AuthContext);
  const provider = getProvider();
  const userRole = localStorage.getItem('userRole');

  const [loading, setLoading] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [showAcknowledgmentWarning, setShowAcknowledgmentWarning] = useState(false);
  const [packDuration, setPackDuration] = useState('30-day');
  const [exudateLevel, setExudateLevel] = useState('');
  const [formData, setFormData] = useState({
    patient_name_or_id: '',
    delivery_address: '',
    delivery_city: '',
    delivery_state: '',
    delivery_zip: '',
    contact_phone: '',
    product_sku: '',
    size: '1g',
    quantity: 1,
    treatment_type: '',
    target_delivery_date: '',
    has_home_health_same_date: null,
  });


  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

   const getRelevantPdfs = () => {
    if (exudateLevel && formData.product_sku) {
      return pdfDocuments[exudateLevel]?.[formData.product_sku] || [];
    }
    return [];
  };

  const canSubmitOrder = () => {
    // Block submission if home health is on same date
    if (formData.has_home_health_same_date === true) {
      return false;
    }


    return true;
  };

  const handleSubmit = async () => {
    if (!acknowledged) {
      setShowAcknowledgmentWarning(true);
      return;
    }

    if(formData.has_home_health_same_date === true) {
      alert('Cannot place order: Patient has home health on the same date or before. Please select a different delivery date.');
      return;
    }

    // Validation
    if (!formData.patient_name_or_id || !formData.delivery_address || !formData.contact_phone ||
        !formData.product_sku || (!formData.size && formData.product_sku === 'collagen_sheet') || !formData.treatment_type || !packDuration || !exudateLevel ||  formData.has_home_health_same_date === null) {
      alert('Please fill in all required fields');
      return;
    }

    

    try {
      setLoading(true);

      const orderData = {
        order_type: 'FAST_ORDER',
        patient_name_or_id: formData.patient_name_or_id,
        street: formData.delivery_address,
        city: formData.delivery_city,
        state: formData.delivery_state,
        zip_code: formData.delivery_zip,
        phone_number: formData.contact_phone,
        product_sku: formData.product_sku,
        size: formData.size,
        quantity: formData.quantity,
        treatment_type: formData.treatment_type,
        target_delivery_date: formData.target_delivery_date,
        pack_duration: packDuration,
        exudateLevel: exudateLevel,
        conservative_care: source === 'conservative_care',
        has_home_health_same_date: formData.has_home_health_same_date,
      };

      if (userRole === 'sales_rep' && selectedProviderId) {
        orderData.provider_id = selectedProviderId;
      }

      const response = await fetch(`${API_BASE_URL}/fast-order/create/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(orderData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.errors ? JSON.stringify(data.errors) : 'Failed to create fast order');
      }

      console.log('✅ Fast order created successfully:', data);
      onSuccess();

    } catch (err) {
      console.error('❌ Error creating fast order:', err);
      alert(`Failed to create order: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      className="px-4 sm:px-6 lg:px-12 pb-8"
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      transition={{ duration: 0.3 }}
    >
      <div className="max-w-3xl mx-auto">
        <motion.div
          className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 p-10 relative overflow-hidden"
          initial={{ scale: 0.95 }}
          animate={{ scale: 1 }}
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-orange-400/10 to-amber-400/10 rounded-full -mr-32 -mt-32 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-amber-400/10 to-yellow-400/10 rounded-full -ml-32 -mb-32 blur-3xl"></div>

          <div className="relative z-10">
            <motion.button
              onClick={onBack}
              className="flex items-center gap-2 text-orange-600 dark:text-orange-400 hover:gap-3 transition-all font-bold mb-6 group"
              whileHover={{ x: -5 }}
            >
              <IoArrowBack className="text-xl group-hover:scale-110 transition-transform" />
              Back
            </motion.button>

            <motion.h2
              className="text-3xl font-black text-gray-900 dark:text-white mb-2 bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              Fast Order (Wholesale)
            </motion.h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8">
              Order products in under 5 minutes
            </p>

            {/* Acknowledgment Box */}
            <motion.div
              className={`mb-8 p-6 rounded-2xl border-2 transition-all ${
                showAcknowledgmentWarning && !acknowledged
                  ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                  : 'border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/20'
              }`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <label className="flex items-start gap-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(e) => {
                    setAcknowledged(e.target.checked);
                    setShowAcknowledgmentWarning(false);
                  }}
                  className="mt-1 w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                />
                <div>
                  <p className="text-sm font-bold text-gray-900 dark:text-white mb-2">
                    Required Acknowledgment <span className="text-red-500">*</span>
                  </p>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    I acknowledge ProMed is not responsible for billing, documentation, or reimbursement for this order. Provider is responsible for reimbursement and compliance.
                  </p>
                </div>
              </label>
              {showAcknowledgmentWarning && !acknowledged && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="mt-3 text-sm text-red-600 dark:text-red-400 font-semibold"
                >
                  ⚠️ You must acknowledge the terms to proceed
                </motion.p>
              )}
            </motion.div>

            {/* Form Fields */}
            <motion.div
              className="space-y-6 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              {/* Patient Name or Internal ID */}
              <div>
                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                  Patient Name or Internal ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.patient_name_or_id}
                  onChange={(e) => handleInputChange('patient_name_or_id', e.target.value)}
                  placeholder="Enter patient name or ID"
                  className="w-full px-5 py-4 bg-gray-50 dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 rounded-2xl text-gray-900 dark:text-white focus:ring-4 focus:ring-orange-500/20 focus:border-orange-500 transition-all font-semibold placeholder:text-gray-400"
                />
              </div>

              {/* Delivery Address */}
              <div>
                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                  Delivery Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.delivery_address}
                  onChange={(e) => handleInputChange('delivery_address', e.target.value)}
                  placeholder="Street address"
                  className="w-full px-5 py-4 bg-gray-50 dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 rounded-2xl text-gray-900 dark:text-white focus:ring-4 focus:ring-orange-500/20 focus:border-orange-500 transition-all font-semibold placeholder:text-gray-400 mb-3"
                />
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="text"
                    value={formData.delivery_city}
                    onChange={(e) => handleInputChange('delivery_city', e.target.value)}
                    placeholder="City"
                    className="px-5 py-4 bg-gray-50 dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 rounded-2xl text-gray-900 dark:text-white focus:ring-4 focus:ring-orange-500/20 focus:border-orange-500 transition-all font-semibold placeholder:text-gray-400"
                  />
                  <input
                    type="text"
                    value={formData.delivery_state}
                    onChange={(e) => handleInputChange('delivery_state', e.target.value)}
                    placeholder="State"
                    className="px-5 py-4 bg-gray-50 dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 rounded-2xl text-gray-900 dark:text-white focus:ring-4 focus:ring-orange-500/20 focus:border-orange-500 transition-all font-semibold placeholder:text-gray-400"
                  />
                </div>
                <input
                  type="text"
                  value={formData.delivery_zip}
                  onChange={(e) => handleInputChange('delivery_zip', e.target.value)}
                  placeholder="Zip Code"
                  className="w-full px-5 py-4 bg-gray-50 dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 rounded-2xl text-gray-900 dark:text-white focus:ring-4 focus:ring-orange-500/20 focus:border-orange-500 transition-all font-semibold placeholder:text-gray-400 mt-3"
                />
              </div>

              {/* Contact Phone */}
              <div>
                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                  Contact Phone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={formData.contact_phone}
                  onChange={(e) => handleInputChange('contact_phone', e.target.value)}
                  placeholder="Phone number"
                  className="w-full px-5 py-4 bg-gray-50 dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 rounded-2xl text-gray-900 dark:text-white focus:ring-4 focus:ring-orange-500/20 focus:border-orange-500 transition-all font-semibold placeholder:text-gray-400"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                  Exudate Level <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-4">
                  {['mild', 'moderate'].map((level) => (
                    <motion.label
                      key={level}
                      className={`cursor-pointer rounded-2xl p-5 border-2 text-center transition-all ${
                        exudateLevel === level
                          ? 'border-orange-500 bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/30 dark:to-amber-900/30 shadow-lg'
                          : 'border-gray-300 dark:border-gray-600 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 hover:border-orange-400 dark:hover:border-orange-500'
                      }`}
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      <input
                        type="radio"
                        name="exudate_level"
                        value={level}
                        checked={exudateLevel === level}
                        onChange={(e) => setExudateLevel(e.target.value)}
                        className="sr-only"
                      />
                      <span className="text-lg font-black text-gray-900 dark:text-white capitalize">
                        {level}
                      </span>
                      {exudateLevel === level && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="mt-2 flex justify-center"
                        >
                          <IoCheckmarkCircle className="text-orange-500 text-2xl" />
                        </motion.div>
                      )}
                    </motion.label>
                  ))}
                </div>
              </div>

              {/* Product SKU */}
              {/* Product SKU */}
<div>
  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
    Product SKU <span className="text-red-500">*</span>
  </label>
  <div className="grid grid-cols-2 gap-4">
    {[
      { value: 'collagen_powder_1g', label: 'Collagen Powder, 1g' },
      { value: 'collagen_sheet', label: 'Collagen Sheet' }
    ].map((product) => (
      <motion.label
        key={product.value}
        className={`cursor-pointer rounded-2xl p-5 border-2 text-center transition-all ${
          formData.product_sku === product.value
            ? 'border-orange-500 bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/30 dark:to-amber-900/30 shadow-lg'
            : 'border-gray-300 dark:border-gray-600 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 hover:border-orange-400 dark:hover:border-orange-500'
        }`}
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.97 }}
      >
        <input
          type="radio"
          name="product_sku"
          value={product.value}
          checked={formData.product_sku === product.value}
          onChange={(e) => handleInputChange('product_sku', e.target.value)}
          className="sr-only"
        />
        <span className="text-base font-black text-gray-900 dark:text-white">
          {product.label}
        </span>
        {formData.product_sku === product.value && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="mt-2 flex justify-center"
          >
            <IoCheckmarkCircle className="text-orange-500 text-2xl" />
          </motion.div>
        )}
      </motion.label>
    ))}
  </div>
</div>

              {/* Size and Quantity */}
              {/* Size and Quantity */}
<div className="grid grid-cols-2 gap-4">
  {formData.product_sku === 'collagen_sheet' && (
  <div>
    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
      Size <span className="text-red-500">*</span>
    </label>
    <select
      value={formData.size}
      onChange={(e) => handleInputChange('size', e.target.value)}
      className="w-full px-5 py-4 bg-gray-50 dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 rounded-2xl text-gray-900 dark:text-white focus:ring-4 focus:ring-orange-500/20 focus:border-orange-500 transition-all font-semibold appearance-none cursor-pointer"
    >
      <option value="">Select size...</option>
      <option value="2x2">2"*2"</option>
      <option value="4x4">4"*4"</option>
      <option value="7x7">7"*7"</option>
    </select>
  </div>
  )}
  {/* <div>
    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
      Quantity <span className="text-red-500">*</span>
    </label>
    <input
      type="number"
      min="1"
      value={formData.quantity}
      onChange={(e) => handleInputChange('quantity', parseInt(e.target.value))}
      className="w-full px-5 py-4 bg-gray-50 dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 rounded-2xl text-gray-900 dark:text-white focus:ring-4 focus:ring-orange-500/20 focus:border-orange-500 transition-all font-semibold"
    />
  </div> */}
</div>

              {/* Treatment Type */}
              <div>
                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                  Treatment Type <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-4">
                  {['POST_OP', 'WOUND'].map((type) => (
                    <motion.label
                      key={type}
                      className={`cursor-pointer rounded-2xl p-5 border-2 text-center transition-all ${
                        formData.treatment_type === type
                          ? 'border-orange-500 bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/30 dark:to-amber-900/30 shadow-lg'
                          : 'border-gray-300 dark:border-gray-600 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 hover:border-orange-400 dark:hover:border-orange-500'
                      }`}
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      <input
                        type="radio"
                        name="treatment_type"
                        value={type}
                        checked={formData.treatment_type === type}
                        onChange={(e) => handleInputChange('treatment_type', e.target.value)}
                        className="sr-only"
                      />
                      <span className="text-lg font-black text-gray-900 dark:text-white">
                        {type === 'POST_OP' ? 'Post-Op' : 'Wound'}
                      </span>
                      {formData.treatment_type === type && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="mt-2 flex justify-center"
                        >
                          <IoCheckmarkCircle className="text-orange-500 text-2xl" />
                        </motion.div>
                      )}
                    </motion.label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                  Do you require a 12 day pack (3x/week) or a 30 day pack (daily dressing change)? <span className="text-red-500">*</span>
                </label>
                <select
                  value={packDuration}
                  onChange={(e) => setPackDuration(e.target.value)}
                  className="w-full px-5 py-4 bg-gray-50 dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 rounded-2xl text-gray-900 dark:text-white focus:ring-4 focus:ring-orange-500/20 focus:border-orange-500 transition-all font-semibold"
                >
                  <option value="30-day">30-day pack (daily dressing change)</option>
                  <option value="15-day">15-day pack (5x/week)</option>
                  <option value="12-day">12-day pack (3x/week)</option>
                </select>
              </div>

              {/* Target Delivery Date */}
              <div>
                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                  Target Delivery Date
                </label>
                <input
                  type="date"
                  value={formData.target_delivery_date}
                  onChange={(e) => handleInputChange('target_delivery_date', e.target.value)}
                  className="w-full px-5 py-4 bg-gray-50 dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 rounded-2xl text-gray-900 dark:text-white focus:ring-4 focus:ring-orange-500/20 focus:border-orange-500 transition-all font-semibold"
                />
              </div>

              {formData.target_delivery_date && (
  <motion.div
    initial={{ opacity: 0, height: 0 }}
    animate={{ opacity: 1, height: 'auto' }}
    exit={{ opacity: 0, height: 0 }}
  >
    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
      Does the patient currently have home health delivered on same date or before? <span className="text-red-500">*</span>
    </label>
    <div className="grid grid-cols-2 gap-4">
      {[
        { value: true, label: 'Yes' },
        { value: false, label: 'No' }
      ].map((option) => (
        <motion.label
          key={option.value.toString()}
          className={`cursor-pointer rounded-2xl p-5 border-2 text-center transition-all ${
            formData.has_home_health_same_date === option.value
              ? 'border-orange-500 bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/30 dark:to-amber-900/30 shadow-lg'
              : 'border-gray-300 dark:border-gray-600 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 hover:border-orange-400 dark:hover:border-orange-500'
          }`}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
        >
          <input
            type="radio"
            name="has_home_health_same_date"
            value={option.value}
            checked={formData.has_home_health_same_date === option.value}
            onChange={(e) => {
              // ✅ Show alert immediately when "Yes" is selected
              if (option.value === true) {
                alert('⚠️ Order Cannot Be Placed\n\nOrders cannot be placed when the patient has home health delivery scheduled on the same date or before the target delivery date.\n\nPlease select a different delivery date or select "No" if home health is scheduled after the target delivery date.');
              }
              handleInputChange('has_home_health_same_date', option.value);
            }}
            className="sr-only"
          />
          <span className="text-lg font-black text-gray-900 dark:text-white">
            {option.label}
          </span>
          {formData.has_home_health_same_date === option.value && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="mt-2 flex justify-center"
            >
              <IoCheckmarkCircle className="text-orange-500 text-2xl" />
            </motion.div>
          )}
        </motion.label>
      ))}
    </div>
  </motion.div>
)}
            </motion.div>

            {exudateLevel && formData.product_sku && (
              <PDFViewer 
                documents={getRelevantPdfs()} 
                exudateLevel={exudateLevel}
                productType={formData.product_sku}
              />
            )}

            {/* Submit Button */}
            <motion.div
              className="flex justify-between gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <motion.button
                onClick={onBack}
                className="px-8 py-4 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-2xl font-bold hover:bg-gray-300 dark:hover:bg-gray-600 transition-all flex items-center gap-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <IoArrowBack />
                Cancel
              </motion.button>
              <motion.button
                onClick={handleSubmit}
                disabled={loading || !canSubmitOrder()}
                className="px-8 py-4 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-2xl font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl flex items-center gap-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {loading ? (
                  <>
                    <motion.div
                      className="w-5 h-5 border-3 border-white border-t-transparent rounded-full"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    />
                    Submitting...
                  </>
                ) : (
                  <>
                    <IoRocketOutline className="text-xl" />
                    Place Order
                  </>
                )}
              </motion.button>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default FastOrderFlow;